/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable, NgZone } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { SnackbarHelper } from '../../../../shared/helpers/snackbar-helper';
import { OrganizationLanguageSummary, SupportedLanguage } from '../../../../shared/models/languages/languages.model';
import { LANGUAGES_DATA_SERVICE, LanguagesDataService } from '../../../../shared/services/languages/languages-data.service';
import * as LanguagesActions from '../languages/languages.actions';

export interface OrganizationLanguagesStateModel {
  organizationLanguageSummary: OrganizationLanguageSummary;
}

@State<OrganizationLanguagesStateModel>({
  name: 'languages',
  defaults: {
    organizationLanguageSummary: undefined,
  },
})
@Injectable()
export class OrganizationLanguagesState {
  @Selector()
  static organizationLanguageSummary({ organizationLanguageSummary }: OrganizationLanguagesStateModel): OrganizationLanguageSummary {
    return organizationLanguageSummary;
  }

  @Selector()
  static organizationSupportedLanguages({ organizationLanguageSummary }: OrganizationLanguagesStateModel): SupportedLanguage[] {
    if (organizationLanguageSummary) {
      return organizationLanguageSummary.supportedLanguages;
    }
    return [];
  }

  constructor(
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    @Inject(LANGUAGES_DATA_SERVICE) private languagesDataService: LanguagesDataService,
    private translocoService: TranslocoService,
  ) {}

  @Action(LanguagesActions.SetOrganizationLanguages)
  setOrganizationLanguages(
    { patchState }: StateContext<OrganizationLanguagesStateModel>,
    { organizationLanguageSummary }: LanguagesActions.SetOrganizationLanguages,
  ) {
    patchState({
      organizationLanguageSummary: organizationLanguageSummary,
    });
  }

  @Action(LanguagesActions.CreateOrganizationLanguages)
  createOrganizationLanguages(
    { patchState }: StateContext<OrganizationLanguagesStateModel>,
    { organizationId, code }: LanguagesActions.CreateOrganizationLanguages,
  ) {
    return this.languagesDataService.createOrganizationLanguages(organizationId, code).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess) {
          patchState({
            organizationLanguageSummary: value,
          });
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            marker('translations.language.message.success.addedToOrganization'),
          );
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(LanguagesActions.RemoveOrganizationLanguages)
  removeOrganizationLanguages(
    { patchState, getState }: StateContext<OrganizationLanguagesStateModel>,
    { organizationId, code }: LanguagesActions.RemoveOrganizationLanguages,
  ) {
    const languageSummary = getState().organizationLanguageSummary;
    return this.languagesDataService.deleteOrganizationLanguages(organizationId, code).pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          patchState({
            organizationLanguageSummary: {
              ...languageSummary,
              supportedLanguages: languageSummary.supportedLanguages.filter((it) => it.language.code !== code),
            },
          });
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            marker('translations.language.message.success.removedFromOrganization'),
          );
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(LanguagesActions.ActivateOrganizationLanguages)
  activateOrganizationLanguages(
    { patchState, getState }: StateContext<OrganizationLanguagesStateModel>,
    { organizationId, code, isActive }: LanguagesActions.ActivateOrganizationLanguages,
  ) {
    const languageSummary = getState().organizationLanguageSummary;
    return this.languagesDataService.languageActivation(organizationId, code, isActive).pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          patchState({
            organizationLanguageSummary: {
              ...languageSummary,
              supportedLanguages: languageSummary.supportedLanguages.map((item) => {
                if (item.language.code === code) {
                  item.isActive = isActive;
                }
                return item;
              }),
            },
          });
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            isActive
              ? marker('translations.language.message.success.activeLanguage')
              : marker('translations.language.message.success.deactiveLanguage'),
          );
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(LanguagesActions.SetDefaultOrganizationLanguages)
  setDefaultOrganizationLanguages(
    { patchState, getState }: StateContext<OrganizationLanguagesStateModel>,
    { organizationId, code }: LanguagesActions.SetDefaultOrganizationLanguages,
  ) {
    const languageSummary = getState().organizationLanguageSummary;
    return this.languagesDataService.setDefaultLanguage(organizationId, code).pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          patchState({
            organizationLanguageSummary: {
              ...languageSummary,
              defaultLanguage: languageSummary.supportedLanguages.find((it) => it.language.code === code).language,
              supportedLanguages: languageSummary.supportedLanguages.map((item) => {
                if (item.language.code === code) {
                  item.isDefault = true;
                }
                return item;
              }),
            },
          });
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            marker('translations.language.message.success.setAsDefault'),
          );
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }
}
